import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Plus, X, Loader2, Save, Edit2, Trash2, CheckCircle2, Search, BookOpen } from 'lucide-react';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

interface Entitlement {
  entitlementID: number;
  employeeID: number;
  employeeName?: string;
  employeeCode?: string;
  departmentName?: string;
  periodID?: number | null;
  periodName?: string;
  maxMealsPerDay: number;
  startDate: string;
  endDate?: string | null;
  notes?: string;
  isActive: boolean;
}
interface Period { periodID: number; periodName: string; }
interface Department { departmentId?: number; id?: number; departmentName?: string; name?: string; }
interface EmpOpt { id: number; firstName?: string; lastName?: string; employeeCode?: string; departmentName?: string; }

const todayStr = () => new Date().toISOString().substring(0, 10);
const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d).substring(0, 10) : dt.toLocaleDateString('tr-TR');
};

const MealEntitlements: React.FC = () => {
  const [tab, setTab] = useState<'list' | 'bulk'>('list');
  const [rows, setRows] = useState<Entitlement[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<EmpOpt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [depFilter, setDepFilter] = useState<number | ''>('');
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Entitlement | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    employeeID: '' as number | '',
    periodID: '' as number | '',
    maxMealsPerDay: 1,
    isActive: true,
    startDate: todayStr(),
    endDate: '',
    notes: '',
  });

  const [bulk, setBulk] = useState({
    departmentIds: [] as number[],
    periodID: '' as number | '',
    maxMealsPerDay: 1,
    startDate: todayStr(),
    endDate: '',
    notes: '',
  });
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const rP: any = await api.get('/meals/periods');
      const rawP = rP?.data ?? rP;
      setPeriods(Array.isArray(rawP) ? rawP : (Array.isArray(rawP?.data) ? rawP.data : []));
    } catch { setPeriods([]); }
    try {
      const rD: any = await api.get('/departments');
      const rawD = rD?.data ?? rD;
      setDepartments(Array.isArray(rawD) ? rawD : (Array.isArray(rawD?.data) ? rawD.data : []));
    } catch { setDepartments([]); }
    try {
      const rE: any = await api.get('/employees');
      const rawE = rE?.data ?? rE;
      const arrE = Array.isArray(rawE) ? rawE : (Array.isArray(rawE?.data) ? rawE.data : []);
      setEmployees(arrE);
    } catch { setEmployees([]); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams();
      if (depFilter) qs.set('departmentId', String(depFilter));
      const r: any = await api.get(`/meals/entitlements${qs.toString() ? `?${qs}` : ''}`);
      const raw = r?.data ?? r;
      const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      setRows(arr);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Haklar yüklenemedi');
    } finally { setLoading(false); }
  }, [depFilter]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    if (!q) return rows;
    return rows.filter(r =>
      (r.employeeName ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
      (r.employeeCode ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
      (r.departmentName ?? '').toLocaleLowerCase('tr-TR').includes(q)
    );
  }, [rows, search]);

  const openNew = () => {
    setEditing(null);
    setForm({ employeeID: '', periodID: '', maxMealsPerDay: 1, isActive: true, startDate: todayStr(), endDate: '', notes: '' });
    setShowModal(true);
  };
  const openEdit = (e: Entitlement) => {
    setEditing(e);
    setForm({
      employeeID: e.employeeID,
      periodID: e.periodID ?? '',
      maxMealsPerDay: e.maxMealsPerDay,
      isActive: e.isActive,
      startDate: String(e.startDate).substring(0, 10),
      endDate: e.endDate ? String(e.endDate).substring(0, 10) : '',
      notes: e.notes ?? '',
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.employeeID) { uyar('Personel seçin'); return; }
    setSaving(true); setError(null);
    try {
      const body: any = {
        employeeID: form.employeeID,
        periodID: form.periodID || null,
        maxMealsPerDay: Number(form.maxMealsPerDay) || 1,
        startDate: form.startDate,
        endDate: form.endDate || null,
        notes: form.notes || null,
        isActive: form.isActive,
      };
      if (editing) {
        await api.put(`/meals/entitlements/${editing.entitlementID}`, body);
      } else {
        await api.post('/meals/entitlements', body);
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const remove = async (e: Entitlement) => {
    if (!(await onayla('Bu hak kaydı silinsin mi?'))) return;
    try {
      await api.delete(`/meals/entitlements/${e.entitlementID}`);
      await load();
    } catch (er: any) {
      setError(er?.response?.data?.message ?? 'Silinemedi');
    }
  };

  const toggleBulkDept = (id: number) => {
    setBulk(b => ({ ...b, departmentIds: b.departmentIds.includes(id) ? b.departmentIds.filter(x => x !== id) : [...b.departmentIds, id] }));
  };

  const runBulk = async () => {
    if (bulk.departmentIds.length === 0) { uyar('En az bir departman seç'); return; }
    setBulkBusy(true); setError(null); setFlash(null);
    try {
      // Backend tek departmanid alıyor — her seçili departman için ayrı çağrı yapalım
      let ok = 0;
      for (const dep of bulk.departmentIds) {
        try {
          const body: any = {
            departmentID: dep,
            periodID: bulk.periodID || null,
            maxMealsPerDay: Number(bulk.maxMealsPerDay) || 1,
            startDate: bulk.startDate,
            endDate: bulk.endDate || null,
            notes: bulk.notes || null,
          };
          await api.post('/meals/entitlements/bulk', body);
          ok++;
        } catch { /* ignore per-dep */ }
      }
      setFlash(`${ok}/${bulk.departmentIds.length} departmana toplu hak atandı`);
      await load();
      setTab('list');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Toplu atama başarısız');
    } finally { setBulkBusy(false); setTimeout(() => setFlash(null), 4000); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <Users size={26} className="text-blue-600" /> Yemek Hakları
          </h1>
          <p className="text-sm text-slate-500 mt-1">Personel bazlı günlük öğün hakları ve toplu atama</p>
        </div>
        <button onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700">
          <Plus size={16} /> Yeni Hak
        </button>
      </div>

      <div className="flex items-center gap-6 border-b border-slate-200">
        <button onClick={() => setTab('list')}
          className={`pb-2 text-sm font-black ${tab === 'list' ? 'text-orange-600 border-b-2 border-orange-600' : 'text-slate-500'}`}>
          Hak Listesi
        </button>
        <button onClick={() => setTab('bulk')}
          className={`pb-2 text-sm font-black flex items-center gap-1 ${tab === 'bulk' ? 'text-orange-600 border-b-2 border-orange-600' : 'text-slate-500'}`}>
          <BookOpen size={14} /> Toplu Atama
        </button>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
      {flash && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm inline-flex items-center gap-2"><CheckCircle2 size={16} /> {flash}</div>}

      {tab === 'list' ? (
        <>
          <div className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Arama</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Personel / Departman" className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Departman</label>
              <select value={depFilter} onChange={e => setDepFilter(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="">Tüm Departmanlar</option>
                {departments.map(d => <option key={d.departmentId ?? d.id} value={d.departmentId ?? d.id}>{d.departmentName ?? d.name}</option>)}
              </select>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[10px] font-black text-slate-500 uppercase tracking-wider">
                    <th className="px-4 py-3">Personel</th>
                    <th className="px-4 py-3">Departman</th>
                    <th className="px-4 py-3">Öğün</th>
                    <th className="px-4 py-3">Günlük Max</th>
                    <th className="px-4 py-3">Başlangıç</th>
                    <th className="px-4 py-3">Bitiş</th>
                    <th className="px-4 py-3">Durum</th>
                    <th className="px-4 py-3 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400"><Loader2 size={20} className="animate-spin mx-auto" /></td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                      <Users size={32} className="mx-auto opacity-30 mb-2" /> Tanımlı hak yok
                    </td></tr>
                  ) : filtered.map(r => (
                    <tr key={r.entitlementID} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-bold text-slate-800">{r.employeeName ?? '—'}</p>
                        <p className="text-[11px] text-slate-500">#{r.employeeCode ?? '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.departmentName ?? '—'}</td>
                      <td className="px-4 py-3 text-slate-700">{r.periodName ?? 'Tümü'}</td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[11px] font-bold">{r.maxMealsPerDay}</span></td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(r.startDate)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(r.endDate)}</td>
                      <td className="px-4 py-3">
                        {r.isActive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold"><CheckCircle2 size={10} /> Aktif</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">Pasif</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => openEdit(r)} className="mr-2 text-blue-600 hover:text-blue-800"><Edit2 size={14} /></button>
                        <button onClick={() => remove(r)} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <h2 className="text-base font-black text-slate-800">Toplu Yemek Hakkı Atama</h2>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-2">Departmanlar</label>
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 grid grid-cols-2 md:grid-cols-3 gap-1">
              {departments.map(d => {
                const id = (d.departmentId ?? d.id)!;
                return (
                  <label key={id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 text-sm">
                    <input type="checkbox" checked={bulk.departmentIds.includes(id)} onChange={() => toggleBulkDept(id)} className="w-4 h-4" />
                    {d.departmentName ?? d.name}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Öğün</label>
              <select value={bulk.periodID} onChange={e => setBulk({ ...bulk, periodID: e.target.value ? Number(e.target.value) : '' })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="">Tümü</option>
                {periods.map(p => <option key={p.periodID} value={p.periodID}>{p.periodName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Günlük Max</label>
              <input type="number" min={1} value={bulk.maxMealsPerDay} onChange={e => setBulk({ ...bulk, maxMealsPerDay: Number(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Başlangıç</label>
              <input type="date" value={bulk.startDate} onChange={e => setBulk({ ...bulk, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Bitiş</label>
              <input type="date" value={bulk.endDate} onChange={e => setBulk({ ...bulk, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
          </div>
          <button onClick={runBulk} disabled={bulkBusy}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700 disabled:opacity-60">
            {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uygula
          </button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-orange-600 text-white px-6 py-4 rounded-t-2xl">
              <h3 className="font-black text-lg">{editing ? 'Hak Düzenle' : 'Yeni Yemek Hakkı'}</h3>
              <button onClick={() => setShowModal(false)} className="hover:bg-white/20 rounded p-1"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Personel *</label>
                <select value={form.employeeID} onChange={e => setForm({ ...form, employeeID: e.target.value ? Number(e.target.value) : '' })}
                  disabled={!!editing}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50">
                  <option value="">Seçiniz</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName} {emp.employeeCode ? `(#${emp.employeeCode})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Öğün</label>
                  <select value={form.periodID} onChange={e => setForm({ ...form, periodID: e.target.value ? Number(e.target.value) : '' })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="">Tüm Öğünler</option>
                    {periods.map(p => <option key={p.periodID} value={p.periodID}>{p.periodName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Günlük Max</label>
                  <input type="number" min={1} value={form.maxMealsPerDay} onChange={e => setForm({ ...form, maxMealsPerDay: Number(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Başlangıç</label>
                  <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Bitiş (ops.)</label>
                  <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Notlar</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4" />
                Aktif
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 p-4">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50">İptal</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-bold hover:bg-orange-700 disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealEntitlements;
